import React, {useRef, useState} from 'react';
import {Button, message, Modal, Space, Table, Tag, Typography, Upload} from 'antd';
import {DeleteOutlined, EyeOutlined, UploadOutlined} from '@ant-design/icons';
import PdfLink from '../../components/PdfLink';
import api from '../../services/api';
import ENDPOINTS from '../../constants/endpoints';
import {formatPath} from '../../constants/PathRoutes';
import {useConfirmModal} from '../../components/ConfirmModal';
import {GREE_1, PRESCRIPTION_STATUS_COLOR} from '../../utils/colors';
import SearchTable from '../../components/admin/SearchTable';
import {BuildFieldErrorsDict, dateFormat} from '../../utils/utils';

export default function ShopPrescriptions() {
    const tableRef = useRef();
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [fileList, setFileList] = useState([]);
    const {showConfirm, ConfirmModal: ConfirmModalComponent} = useConfirmModal();

    const handleDelete = (record) => {
        showConfirm({
            title: 'Excluir Prescrição',
            description: 'Tem certeza que deseja excluir esta prescrição?',
            type: 'danger',
            onConfirm: async () => {
                await api.delete(formatPath(ENDPOINTS.ECOMMERCE_PRESCRIPTION_DETAIL, {id: record.id}));
                message.success('Prescrição excluída com sucesso');
                tableRef.current?.refetch();
            }
        });
    };

    const handleUpload = async () => {
        if (fileList.length === 0) {
            message.warning('Selecione um arquivo PDF');
            return;
        }

        const formData = new FormData();
        formData.append('prescription_file', fileList[0].originFileObj || fileList[0]);

        setUploading(true);
        try {
            await api.post(ENDPOINTS.ECOMMERCE_PRESCRIPTION_LIST, formData, {
                headers: {'Content-Type': 'multipart/form-data'}
            });
            message.success('Prescrição enviada com sucesso');
            setUploadModalOpen(false);
            setFileList([]);
            tableRef.current?.refetch();
        } catch (error) {
            BuildFieldErrorsDict(error)

        } finally {
            setUploading(false);
        }
    };

    const columns = [

        {
            title: 'Desconto',
            dataIndex: 'discount_value',
            key: 'discount_value',
            width: 100,
            render: (value) => value ? `${Number(value).toFixed(2)} %` : '-',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status, record) => {
                const color = PRESCRIPTION_STATUS_COLOR[status] ?? 'default';
                return <Tag color={color}>{record.status_display ?? '-'}</Tag>;
            },
        },
        {
            title: 'CRM / CRMV',
            key: 'doctor',
            width: 100,
            render: (_, record) => {
                const parts = [];
                if (record.doctor_crm) parts.push(`CRM: ${record.doctor_crm}`);
                if (record.doctor_crmv) parts.push(`CRMV: ${record.doctor_crmv}`);
                return parts.length > 0 ? parts.join(' | ') : '-';
            },
        },
        {
            title: 'Validade',
            dataIndex: 'expiration_date',
            key: 'expiration_date',
            render: (date) => dateFormat(date) || '-',
            width: 110,
        },
        {
            title: 'Observações',
            dataIndex: 'notes',
            key: 'notes',
            ellipsis: true,
            render: (text) => text || '-',
        },
        {
            title: 'Ações',
            key: 'actions',
            width: 100,
            render: (_, record) => (
                <Space>
                    {record.prescription_file && (
                        <Button
                            size="small"
                            icon={<EyeOutlined/>}
                            href={record.prescription_file}
                            target="_blank"
                        />
                    )}
                    {!record.expiration_date && (
                        <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined/>}
                            onClick={() => handleDelete(record)}
                        />
                    )}
                </Space>
            ),
        },
    ];

    const expandable = {
        expandedRowRender: (record) => (
            <Table
                size="small"
                pagination={false}
                dataSource={record.items}
                rowKey="id"
                columns={[
                    {title: 'Produto', dataIndex: 'product_name', key: 'product_name'},
                    {title: 'Quantidade', dataIndex: 'quantity', key: 'quantity', width: 120},
                ]}
            />
        ),
        rowExpandable: (record) => record.items?.length > 0,
    };

    return (
        <>
            <SearchTable
                ref={tableRef}
                url={ENDPOINTS.ECOMMERCE_PRESCRIPTION_LIST}
                columns={columns}
                title="Minhas Prescrições"
                showSearch={false}
                expandable={expandable}
                buttonProps={{
                    label: 'Nova Prescrição',
                    onClick: () => setUploadModalOpen(true),
                }}
            />

            <Modal
                title="Enviar Prescrição"
                open={uploadModalOpen}
                onCancel={() => {
                    setUploadModalOpen(false);
                    setFileList([]);
                }}
                onOk={handleUpload}
                okText="Enviar"
                cancelText="Cancelar"
                confirmLoading={uploading}
                okButtonProps={{style: {background: GREE_1, borderColor: GREE_1}}}
            >
                <Typography.Paragraph type="secondary">
                    Envie o arquivo PDF da sua prescrição médica. Após o envio, a prescrição será analisada pela equipe.
                </Typography.Paragraph>
                <Upload
                    accept=".pdf"
                    maxCount={1}
                    fileList={fileList}
                    onChange={({fileList: newFileList}) => setFileList(newFileList)}
                    beforeUpload={() => false}
                >
                    <Button icon={<UploadOutlined/>}>Selecionar PDF</Button>
                </Upload>
            </Modal>

            <ConfirmModalComponent/>
        </>
    );
}
